import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ShieldCheck, KeyRound, ArrowLeft } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { ApiError } from '@/lib/api-client';
import { AuthShell } from '@/components/AuthShell';
import { CodeInput } from '@/components/CodeInput';

export function TwoFactor() {
  const navigate = useNavigate();
  const { state, totpVerify, recoveryVerify, cancelLogin } = useAuth();
  const { t } = useTranslation('auth');
  const [mode, setMode] = useState<'code' | 'recovery'>('code');
  const [code, setCode] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [rememberDevice, setRememberDevice] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  if (state.status === 'authenticated') return <Navigate to="/" replace />;
  if (state.status !== 'totp_verify') return <Navigate to="/login" replace />;

  // The backend's 401s for an expired/rotated temp token and rate-limit 429s
  // are not the user's fault — never blame their authenticator code for them.
  function describeError(err: unknown): string {
    if (err instanceof ApiError) {
      if (err.status === 429) return err.message;
      if (err.status === 401 && /temp token/i.test(err.message)) return t('twofactor.sessionExpired');
    }
    return t('twofactor.invalidCode');
  }

  async function submitCode(value?: string) {
    const codeToUse = value ?? code;
    if (codeToUse.length !== 6 || isLoading) return;
    setError('');
    setIsLoading(true);
    try {
      await totpVerify(codeToUse, rememberDevice);
    } catch (err) {
      setError(describeError(err));
      setCode('');
      setIsLoading(false);
    }
  }

  async function submitRecovery(e: React.FormEvent) {
    e.preventDefault();
    if (!recoveryCode.trim() || isLoading) return;
    setError('');
    setIsLoading(true);
    try {
      await recoveryVerify(recoveryCode.trim(), rememberDevice);
    } catch (err) {
      setError(describeError(err));
      setIsLoading(false);
    }
  }

  return (
    <AuthShell>
      <div className="flex items-start gap-3">
        <div className="p-2.5 rounded-xl bg-white/10 border border-white/[0.15]">
          {mode === 'code' ? (
            <ShieldCheck className="w-6 h-6 text-white" />
          ) : (
            <KeyRound className="w-6 h-6 text-white" />
          )}
        </div>
        <div>
          <h2 className="text-xl font-bold text-white">
            {mode === 'code' ? t('twofactor.title') : t('twofactor.recoveryTitle')}
          </h2>
          <p className="text-white/60 mt-1 text-sm">
            {mode === 'code' ? t('twofactor.subtitle') : t('twofactor.recoverySubtitle')}
          </p>
        </div>
      </div>

      {error && (
        <div className="px-4 py-2.5 rounded-lg bg-red-500/20 border border-red-400/30 text-red-200 text-sm font-medium">
          {error}
        </div>
      )}

      {mode === 'code' ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void submitCode();
          }}
          className="space-y-5"
        >
          <CodeInput value={code} onChange={setCode} onComplete={(v) => void submitCode(v)} disabled={isLoading} />

          <label className="flex items-center gap-2.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={rememberDevice}
              onChange={(e) => setRememberDevice(e.target.checked)}
              className="w-4 h-4 rounded accent-[#ed125f]"
            />
            <span className="text-sm text-white/70">{t('twofactor.rememberDevice')}</span>
          </label>

          <button
            type="submit"
            disabled={isLoading || code.length !== 6}
            className="w-full py-2.5 rounded-lg text-white font-semibold text-sm shadow-lg bg-gradient-to-r from-brand-rose to-brand-orange hover:from-brand-purple hover:to-brand-rose transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isLoading && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
            {isLoading ? t('twofactor.verifying') : t('twofactor.verify')}
          </button>

          <button
            type="button"
            onClick={() => {
              setMode('recovery');
              setError('');
            }}
            className="w-full text-center text-xs font-medium text-white/60 hover:text-white transition-colors underline underline-offset-2"
          >
            {t('twofactor.useRecovery')}
          </button>
        </form>
      ) : (
        <form onSubmit={(e) => void submitRecovery(e)} className="space-y-5">
          <input
            type="text"
            value={recoveryCode}
            onChange={(e) => setRecoveryCode(e.target.value.toUpperCase())}
            placeholder={t('twofactor.recoveryPlaceholder')}
            disabled={isLoading}
            autoComplete="off"
            className="glass-input w-full px-4 py-3 rounded-lg text-base font-mono tracking-[0.2em] text-center text-white placeholder:text-white/40 placeholder:tracking-normal focus:outline-none focus:border-brand-rose focus:bg-white/[0.22] focus:ring-[3px] focus:ring-brand-rose/25 disabled:opacity-50"
          />

          <label className="flex items-center gap-2.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={rememberDevice}
              onChange={(e) => setRememberDevice(e.target.checked)}
              className="w-4 h-4 rounded accent-[#ed125f]"
            />
            <span className="text-sm text-white/70">{t('twofactor.rememberDevice')}</span>
          </label>

          <button
            type="submit"
            disabled={isLoading || !recoveryCode.trim()}
            className="w-full py-2.5 rounded-lg text-white font-semibold text-sm shadow-lg bg-gradient-to-r from-brand-rose to-brand-orange hover:from-brand-purple hover:to-brand-rose transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isLoading && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
            {isLoading ? t('twofactor.verifying') : t('twofactor.verify')}
          </button>

          <button
            type="button"
            onClick={() => {
              setMode('code');
              setError('');
            }}
            className="w-full flex items-center justify-center gap-1.5 text-xs font-medium text-white/60 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            {t('twofactor.backToCode')}
          </button>
        </form>
      )}

      <button
        type="button"
        onClick={() => {
          cancelLogin(); // reset the mid-login state so /login renders the form
          void navigate('/login');
        }}
        className="w-full text-center text-xs text-white/35 hover:text-white/60 transition-colors"
      >
        {t('twofactor.backToLogin')}
      </button>
    </AuthShell>
  );
}
